import moment from 'moment';
import React from 'react';
import {
  ActivityIndicator,
  FlatList,
  Text,
  View,
  StyleSheet,
} from 'react-native';
import {Card} from 'react-native-paper';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {useGetInHouseQuery} from '../redux/services/foreportsApi';
import {RootState} from '../redux/store';
import {theme} from '../theme/theme';

import T from '../Components/T';
const InHouse = () => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;

  const {data, isLoading} = useGetInHouseQuery(cid);

  const rows = data?.map((value: any, key: any) => ({
    id: key.toString(),
    ci_no: value.ci_no,
    ci_date: moment(value.ci_date).utcOffset('+05:30').format('DD-MM-YY HH:mm'),
    rm_name: value.rm_name,
    ab_mobile: value.ab_mobile,
    ab_name: value.ab_name,
    ab_cname: value.ab_cname,
    ab_city: value.ab_city,
    ab_vehicle: value.ab_vehicleno,
    ci_arrvial: moment(value.ci_arrvial)
      .utcOffset('+05:30')
      .format('DD-MM-YY HH:mm'),
    ci_depature: moment(value.ci_depature)
      .utcOffset('+05:30')
      .format('DD-MM-YY HH:mm'),
  }));

  if (isLoading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}><T>Loading guests...</T></Text>
      </View>
    );
  }

  const totalGuests = rows?.length || 0;

  const renderGuestCard = ({item}: {item: any}) => (
    <Card style={styles.guestCard} elevation={3}>
      <View style={styles.cardHeader}>
        <View style={styles.folioBadge}>
          <FontAwesome name="file-text-o" size={14} color={theme.colors.white} />
          <Text style={styles.folioText}><T>Folio #</T>{item.ci_no}</Text>
        </View>
        <View style={styles.roomBadge}>
          <FontAwesome name="bed" size={14} color={theme.colors.primary} />
          <Text style={styles.roomText}>{item.rm_name}</Text>
        </View>
      </View>

      <View style={styles.cardBody}>
        <View style={styles.guestInfo}>
          <View style={styles.infoRow}>
            <View style={styles.infoIcon}>
              <FontAwesome name="user" size={16} color={theme.colors.primary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}><T>Guest Name</T></Text>
              <Text style={styles.infoValue}>{item.ab_name || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.infoRow}>
            <View style={styles.infoIcon}>
              <FontAwesome name="phone" size={16} color={theme.colors.success} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}><T>Mobile</T></Text>
              <Text style={styles.infoValue}>{item.ab_mobile || 'N/A'}</Text>
            </View>
          </View>

          {item.ab_cname && (
            <View style={styles.infoRow}>
              <View style={styles.infoIcon}>
                <FontAwesome
                  name="building"
                  size={16}
                  color={theme.colors.info}
                />
              </View>
              <View style={styles.infoContent}>
                <Text style={styles.infoLabel}><T>Company</T></Text>
                <Text style={styles.infoValue}>{item.ab_cname}</Text>
              </View>
            </View>
          )}

          {item.ab_city && (
            <View style={styles.infoRow}>
              <View style={styles.infoIcon}>
                <FontAwesome
                  name="map-marker"
                  size={16}
                  color={theme.colors.warning}
                />
              </View>
              <View style={styles.infoContent}>
                <Text style={styles.infoLabel}><T>City</T></Text>
                <Text style={styles.infoValue}>{item.ab_city}</Text>
              </View>
            </View>
          )}

          {item.ab_vehicle && (
            <View style={styles.infoRow}>
              <View style={styles.infoIcon}>
                <FontAwesome
                  name="car"
                  size={16}
                  color={theme.colors.secondary}
                />
              </View>
              <View style={styles.infoContent}>
                <Text style={styles.infoLabel}><T>Vehicle</T></Text>
                <Text style={styles.infoValue}>{item.ab_vehicle}</Text>
              </View>
            </View>
          )}
        </View>

        <View style={styles.timelineContainer}>
          <View style={styles.timelineItem}>
            <View style={[styles.timelineDot, styles.arrivalDot]} />
            <View style={styles.timelineContent}>
              <Text style={styles.timelineLabel}><T>Arrival</T></Text>
              <Text style={styles.timelineValue}>{item.ci_arrvial}</Text>
            </View>
          </View>
          <View style={styles.timelineItem}>
            <View style={[styles.timelineDot, styles.departureDot]} />
            <View style={styles.timelineContent}>
              <Text style={styles.timelineLabel}><T>Departure</T></Text>
              <Text style={styles.timelineValue}>{item.ci_depature}</Text>
            </View>
          </View>
        </View>
      </View>
    </Card>
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.headerIconContainer}>
            <FontAwesome
              name="users"
              size={28}
              color={theme.colors.white}
            />
          </View>
          <View style={styles.headerTextContainer}>
            <Text style={styles.headerTitle}><T>In-House Guests</T></Text>
            <Text style={styles.headerSubtitle}>
              {totalGuests} {totalGuests === 1 ? 'guest' : 'guests'} <T>currently
              staying</T>
            </Text>
          </View>
        </View>
      </View>

      {totalGuests === 0 ? (
        <View style={styles.emptyContainer}>
          <FontAwesome
            name="bed"
            size={64}
            color={theme.colors.mediumGray}
          />
          <Text style={styles.emptyText}><T>No guests in-house</T></Text>
          <Text style={styles.emptySubtext}>
            <T>All rooms are currently vacant</T>
          </Text>
        </View>
      ) : (
        <FlatList
          data={rows}
          keyExtractor={item => item.id}
          renderItem={renderGuestCard}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.backgroundSecondary,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.backgroundSecondary,
  },
  loadingText: {
    marginTop: theme.spacing.base,
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.textSecondary,
    fontWeight: theme.typography.fontWeight.medium,
  },
  header: {
    backgroundColor: theme.colors.primary,
    paddingTop: theme.spacing['2xl'],
    paddingBottom: theme.spacing.xl,
    paddingHorizontal: theme.spacing.xl,
    ...theme.shadows.md,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIconContainer: {
    width: 56,
    height: 56,
    borderRadius: theme.borderRadius.full,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.base,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.white,
    marginBottom: theme.spacing.xs,
  },
  headerSubtitle: {
    fontSize: theme.typography.fontSize.sm,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: theme.typography.fontWeight.regular,
  },
  listContent: {
    padding: theme.spacing.base,
    paddingBottom: 80,
  },
  guestCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.xl,
    marginBottom: theme.spacing.base,
    overflow: 'hidden',
    ...theme.shadows.md,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.base,
    backgroundColor: theme.colors.backgroundSecondary,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderLight,
  },
  folioBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary,
    paddingHorizontal: theme.spacing.base,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
  },
  folioText: {
    color: theme.colors.white,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    marginLeft: theme.spacing.xs,
  },
  roomBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.infoLight,
    paddingHorizontal: theme.spacing.base,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
  },
  roomText: {
    color: theme.colors.primary,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    marginLeft: theme.spacing.xs,
  },
  cardBody: {
    padding: theme.spacing.base,
  },
  guestInfo: {
    marginBottom: theme.spacing.base,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: theme.spacing.base,
  },
  infoIcon: {
    width: 32,
    height: 32,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.backgroundSecondary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.base,
  },
  infoContent: {
    flex: 1,
  },
  infoLabel: {
    fontSize: theme.typography.fontSize.xs,
    color: theme.colors.textTertiary,
    fontWeight: theme.typography.fontWeight.medium,
    marginBottom: theme.spacing.xs / 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  infoValue: {
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.textPrimary,
    fontWeight: theme.typography.fontWeight.medium,
  },
  timelineContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: theme.spacing.base,
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderLight,
  },
  timelineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: theme.borderRadius.full,
    marginRight: theme.spacing.base,
  },
  arrivalDot: {
    backgroundColor: theme.colors.success,
  },
  departureDot: {
    backgroundColor: theme.colors.error,
  },
  timelineContent: {
    flex: 1,
  },
  timelineLabel: {
    fontSize: theme.typography.fontSize.xs,
    color: theme.colors.textTertiary,
    fontWeight: theme.typography.fontWeight.medium,
    marginBottom: theme.spacing.xs / 2,
  },
  timelineValue: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textPrimary,
    fontWeight: theme.typography.fontWeight.semibold,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing['2xl'],
  },
  emptyText: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.base,
    marginBottom: theme.spacing.xs,
  },
  emptySubtext: {
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.textTertiary,
    textAlign: 'center',
  },
});

export default InHouse;

